/** Dialer / Comms (v1 dialer-comms.js) — SMS threads, click-to-call, and inbound
 * history, over the ALREADY-DEPLOYED Quo backend (flipbase-clone/backend
 * routes/comms.py, prefix /api/quo). Reads (status / numbers / comms threads)
 * are safe and ungated; SEND and CALL fire real actions and are gated behind
 * liveFire quoSend / quoCall (OFF until the supervised sit-down). No backend
 * changes — this is v2 UI onto endpoints that already run. */
import { backendFetch } from "../../lib/backend";
import { fmtAppTz } from "../../lib/apptime";
import { API_BASE } from "../../config";

export interface QuoStatus {
  apiKeySet: boolean;
  webhookSecretSet: boolean;
  supabase: boolean;
}
export interface QuoNumber {
  id: string;
  name: string;
  numbers: string[];
}

/** One row of the backend `comms` table (send_sms / webhook shape). */
export interface CommRow {
  id: string;
  direction: "in" | "out";
  channel: "sms" | "call";
  contactNumber: string;
  contactName: string;
  body: string;
  status: string;
  createdAt: number; // epoch ms
  duration: number | null; // seconds, calls only
  leadKey: string | null;
  fromNumber: string; // sender line (raw from_number)
  toNumber: string; // recipient line (raw to_number)
}

/** Which of YOUR Quo business lines this message is on: inbound landed on the
 * `to` line; outbound left from the `from` line. "" when the backend row
 * predates from/to capture (older rows bucket under "no line"). */
export function commLine(c: CommRow): string {
  return c.direction === "in" ? c.toNumber : c.fromNumber;
}

const s = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};

export function narrowComm(raw: unknown): CommRow | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = s(o["id"]) || s(o["dialpad_id"]);
  const contactNumber = s(o["contact_number"]);
  const body = s(o["body"] ?? o["text"]);
  const channel = s(o["channel"]) === "call" ? "call" : "sms";
  // a call row can have an empty body; an sms row with no body/number is junk
  if (!contactNumber && !body) return null;
  const created = s(o["created_at"]);
  const t = created ? Date.parse(created) : NaN;
  return {
    id: id || contactNumber + ":" + created,
    direction: s(o["direction"]) === "in" ? "in" : "out",
    channel,
    contactNumber,
    contactName: s(o["contact_name"]),
    body,
    status: s(o["status"]),
    createdAt: Number.isNaN(t) ? 0 : t,
    duration: numOrNull(o["duration"]),
    leadKey: s(o["lead_key"]) || null,
    fromNumber: s(o["from_number"]),
    toNumber: s(o["to_number"]),
  };
}

// ---- reads (ungated) --------------------------------------------------------

export async function fetchQuoStatus(token: string | null, signal?: AbortSignal): Promise<QuoStatus> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/quo/status", { timeoutMs: 30000, ...(signal ? { signal } : {}) });
  return { apiKeySet: j["api_key_set"] === true, webhookSecretSet: j["webhook_secret_set"] === true, supabase: j["supabase"] === true };
}

export async function fetchQuoNumbers(token: string | null, signal?: AbortSignal): Promise<QuoNumber[]> {
  const j = await backendFetch<{ users?: unknown[] }>(token, "/api/quo/users", { timeoutMs: 30000, ...(signal ? { signal } : {}) });
  const out: QuoNumber[] = [];
  for (const u of Array.isArray(j.users) ? j.users : []) {
    if (!u || typeof u !== "object") continue;
    const o = u as Record<string, unknown>;
    const numbers = Array.isArray(o["numbers"]) ? o["numbers"].map(s).filter(Boolean) : [];
    out.push({ id: s(o["id"]), name: s(o["name"]) || (numbers[0] ?? ""), numbers });
  }
  return out;
}

export async function fetchComms(
  token: string | null,
  opts: { leadKey?: string; number?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<CommRow[]> {
  const p = new URLSearchParams();
  if (opts.leadKey) p.set("lead_key", opts.leadKey);
  else if (opts.number) p.set("number", opts.number);
  p.set("limit", String(opts.limit ?? 500));
  const j = await backendFetch<{ comms?: unknown[] }>(token, "/api/quo/comms?" + p.toString(), { timeoutMs: 40000, ...(signal ? { signal } : {}) });
  const rows = (Array.isArray(j.comms) ? j.comms : []).map(narrowComm).filter((c): c is CommRow => c !== null);
  rows.sort((a, b) => a.createdAt - b.createdAt);
  return rows;
}

// ---- writes (GATED — caller must check liveOn first) ------------------------

export async function sendSms(
  token: string | null,
  body: { org_id: string; to: string; text: string; lead_key?: string; contact_name?: string; from_number?: string; idem_key?: string },
): Promise<{ ok: boolean; comm: unknown }> {
  return backendFetch(token, "/api/quo/sms", { method: "POST", body, timeoutMs: 45000 });
}

export async function placeCall(
  token: string | null,
  body: { org_id: string; to: string; lead_key?: string; contact_name?: string; from_number?: string },
): Promise<unknown> {
  return backendFetch(token, "/api/quo/call", { method: "POST", body, timeoutMs: 45000 });
}

// ---- webhook registration (setup — Quo SMS settings wizard) -----------------
// (Re)registers the inbound SMS + call webhooks with Quo, pointing them at THIS
// backend (base_url = API_BASE), exactly like v1's dpRegisterWebhook. Idempotent:
// v1 already registers to the same backend, so re-running just re-points inbound
// to the same place — it never texts or calls anyone. The Settings wizard enables
// it only once QUO_API_KEY is set (the backend needs the Quo key to register); on
// failure the server detail surfaces (v1's "check QUO_API_KEY"). No new backend —
// the endpoint already runs (HANDOFF: quo/…/register-webhook).
export interface QuoRegisterResult { ok: boolean; raw: unknown; }
export async function registerQuoWebhooks(token: string | null): Promise<QuoRegisterResult> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/quo/register-webhook", {
    method: "POST",
    body: { base_url: API_BASE },
    timeoutMs: 45000,
  });
  return { ok: j["ok"] === true, raw: j };
}

// ---- thread grouping (v1 dialer thread list) --------------------------------

export interface Thread {
  number: string;
  name: string;
  messages: CommRow[];
  last: CommRow;
  unreadIn: number; // inbound count (for a simple badge)
  lines: string[]; // distinct business (Quo) lines this conversation is on
}

/** Group comms by contact number into conversation threads, most-recent first.
 * Each thread also carries the distinct business lines it spans, so the comms
 * page can filter by "which of my numbers". */
export function groupThreads(comms: CommRow[]): Thread[] {
  const by = new Map<string, CommRow[]>();
  for (const c of comms) {
    const k = c.contactNumber || "unknown";
    const arr = by.get(k);
    if (arr) arr.push(c);
    else by.set(k, [c]);
  }
  const threads: Thread[] = [];
  for (const [number, msgs] of by) {
    msgs.sort((a, b) => a.createdAt - b.createdAt);
    const last = msgs[msgs.length - 1];
    if (!last) continue;
    const named = [...msgs].reverse().find((m) => m.contactName);
    const lines = [...new Set(msgs.map(commLine).filter(Boolean))];
    threads.push({
      number,
      name: named?.contactName || number,
      messages: msgs,
      last,
      unreadIn: msgs.filter((m) => m.direction === "in").length,
      lines,
    });
  }
  threads.sort((a, b) => b.last.createdAt - a.last.createdAt);
  return threads;
}

/** All distinct business lines across every thread, most-used first — the
 * options for the comms page's line filter. */
export function allLines(threads: Thread[]): string[] {
  const count = new Map<string, number>();
  for (const t of threads) for (const ln of t.lines) count.set(ln, (count.get(ln) ?? 0) + 1);
  return [...count.entries()].sort((a, b) => b[1] - a[1]).map(([ln]) => ln);
}

/** v1 _commsTime: short relative-ish label from an epoch. The "x ago" part is
 * timezone-free arithmetic; the date fallback is rendered in EASTERN so a
 * message stamped 11pm Monday doesn't read as Tuesday on a device in another
 * zone. Beyond a year the year is shown — "Dec 2" with no year is how eight
 * months of staleness hid in plain sight in the Inbox. */
export function commTime(ms: number): string {
  if (!ms) return "";
  const now = Date.now();
  const diff = now - ms;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return Math.floor(diff / 60000) + "m ago";
  if (diff < 86400000) return Math.floor(diff / 3600000) + "h ago";
  if (diff < 7 * 86400000) return Math.floor(diff / 86400000) + "d ago";
  return fmtAppTz(ms, diff < 330 * 86400000
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" });
}

/** v1 call-duration label. */
export function callDuration(sec: number | null): string {
  if (sec === null || sec <= 0) return "";
  const m = Math.floor(sec / 60);
  const s2 = sec % 60;
  return m ? `${m}m ${s2}s` : `${s2}s`;
}

// ---- SMS quick templates (v1 dialer-comms.js message templates) -------------
// Canned messages with {name}/{firstName} interpolation. Stored per-device in
// localStorage (the settings write-door doesn't whitelist a templates key, and
// we don't touch the backend) — a personal compose convenience; sending itself
// stays gated by liveFire quoSend.

export interface SmsTemplate { label: string; text: string; }

const SMS_TPL_KEY = "SMS_TEMPLATES";
const tstr = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

/** Parse a raw templates array (tolerant of junk); self-labels textless labels. */
export function parseSmsTemplates(v: unknown): SmsTemplate[] {
  if (!Array.isArray(v)) return [];
  const out: SmsTemplate[] = [];
  for (const e of v) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const text = tstr(o["text"]).trim();
    if (!text) continue;
    out.push({ label: tstr(o["label"]).trim() || text.slice(0, 24), text });
  }
  return out;
}

/** Load this device's SMS templates from localStorage. */
export function loadSmsTemplates(): SmsTemplate[] {
  try {
    if (typeof localStorage === "undefined") return [];
    const raw = localStorage.getItem(SMS_TPL_KEY);
    return raw ? parseSmsTemplates(JSON.parse(raw)) : [];
  } catch { return []; }
}

/** Persist this device's SMS templates to localStorage. */
export function saveSmsTemplates(list: SmsTemplate[]): void {
  try { if (typeof localStorage !== "undefined") localStorage.setItem(SMS_TPL_KEY, JSON.stringify(list)); } catch { /* quota / private mode — non-fatal */ }
}

/** Interpolate {name} (and {firstName}) into a template body. Unknown tokens are
 * left intact so a half-filled template is obvious rather than silently blank. */
export function renderSmsTemplate(text: string, ctx: { name?: string }): string {
  const name = (ctx.name ?? "").trim();
  const first = name.split(/\s+/)[0] ?? "";
  return text
    .replace(/\{name\}/gi, name || "{name}")
    .replace(/\{firstName\}/gi, first || "{firstName}");
}
